// The front page's words (02-DESIGN/03-console/03-the-signed-out-site.md
// § the page, in order; decision 0071). The page tells; it does not explain:
// it speaks in the second person from positions a visitor recognises, with
// candour and without superlatives, and every sentence about the platform
// traces to the mission, a feature or a design (§ where the words come from).
// Kept apart from the page so that the site can move to a repository of its
// own whole (decision 0054).

/** The category line: the page's one h1 (decision 0071 § 2). */
export const HEADLINE = "The agentic cloud.";

export const LEDE =
  "The cloud gave software a place to run. Software that acts needs a place to act, within what your organisation decides. That is Impire: connect any app or agent as it is, decide once, and keep the record of everything that ran.";

/** The opening's one filled teal button, and the card's primary on this page, where the install allows sign-up. */
export const GET_STARTED = "Get started";

/** The opening's button where sign-up is off and the profile names `request_access` (decisions 0074 and 0076): the form's title too. */
export const REQUEST_ACCESS = "Request access";

/** The request form's words (§ the request form; decision 0076). */
export const REQUEST_LEAD = "Tell us who you are and what you want to run. We answer by mail.";
export const REQUEST_FIELDS = {
  name: "Your name",
  email: "Work email",
  organisation: "Organisation",
  message: "What you want to run",
} as const;
export const SEND_REQUEST = "Send request";
export const REQUEST_SENDING = "Sending…";
export const CANCEL = "Cancel";
export const BACK = "Back";
export const OR_WRITE_TO = "or write to";
export const REQUEST_SENT = {
  title: "Thanks.",
  text: (email: string) => `We read every request and answer at ${email}.`,
} as const;
/** The one sentence over the actions, by what the platform answered; never the platform's own text. */
export const REQUEST_REFUSED: Record<
  "name" | "email" | "message" | "limit" | "unavailable",
  (address: string) => string
> = {
  name: () => "Type your name.",
  email: () => "That is not an email address.",
  message: () => "Say what you want to run.",
  limit: (address) => `Too many requests for now; try again in an hour, or write to ${address}.`,
  unavailable: (address) => `The request could not be sent; try again later, or write to ${address}.`,
};

export const SIGN_IN_TO_WORKSPACE = "Sign in to your workspace";

/** The line under the opening's actions. */
export const UNDER_ACTIONS = "Signing in creates a workspace for you.";

/** The interfaces an app already speaks: the only names on the page that are not Impire's own or a provider's. */
export const INTERFACES: readonly string[] = ["OpenAI API", "Anthropic API", "MCP"];

/** The top line's links. */
export const WHY = "Why Impire";
export const SIGN_IN = "Sign in";

/** The visual's name and description, for a screen reader (§ for keyboard and screen reader). */
export const VISUAL_NAME = "The agentic cloud, drawn";
export const VISUAL_DESCRIPTION =
  "A cloud drawn as a wireframe of points joined to their nearest neighbours, turning slowly, with a stream of dots flowing into its base and signals travelling along its edges.";

/** A heading and the paragraph under it. */
export interface Part {
  readonly heading: string;
  readonly text: string;
}

export const STATEMENT: Part = {
  heading: "There is a cost to being wrong. It keeps most organisations from choosing at all.",
  text: "Every AI app you allow has to be approved, given a model, fenced with rules and logged, one app at a time. So the answer becomes not yet, and the people who would have moved first wait. Impire does that work once, for the workspace. Whatever connects is held to what you decided, and when you change your mind, the change applies everywhere, the same day. We make being wrong cheap to fix, so you can afford to choose.",
};

/** The four positions, in order: the developer, the employee, the one who answers for it, the builder on Impire. */
export const POSITIONS: readonly Part[] = [
  {
    heading: "As a developer, you know how fast this moves.",
    text: "The stack you picked in spring is not the one you would pick now, so do not marry it. Build against the OpenAI or Anthropic API and MCP, test it anywhere, and connect it to an organisation's Impire unchanged. Logging, approval, secrets and sign-in are the platform's job, not yours. Your app stays an app.",
  },
  {
    heading: "You found an app that saves you an afternoon a week.",
    text: "You should not need a meeting to use it. Connect it to your workspace and start today. It runs within what your organisation already decided, which models, which tools, which rules, so nobody has to say no, and you do not have to ask twice.",
  },
  {
    heading: "You are the one who has to answer for it.",
    text: "Then you want it on the record, not on trust. Every call that runs in a workspace is written down, who asked, through which app, what, and how it ended, in a chain where a gap or an edit shows. Secrets live in the workspace's own vault, sealed end to end. Every workload runs in a microVM of its own. When something needs a person's yes first, it waits for one.",
  },
  {
    heading: "Build it once. Let every organisation run it as its own.",
    text: "Publish a solution on Impire and an organisation installs it into its own workspace, with what it may reach shown first. It runs there without you operating it or seeing their data. Sign-in, rules and the record are already in place, so you ship the part only you can build.",
  },
];

export const ALREADY_HEADING = "Already there when you connect.";

/** The eight short lines, with no explanation under them. */
export const ALREADY: readonly string[] = [
  "Any model provider, or a server you run",
  "Tools over MCP, acting as the person who asked",
  "Rules on every call: allow, deny, or ask a person",
  "Sign-in with the identity provider you already have",
  "A sealed vault per workspace",
  "A microVM per workload",
  "A chained record of everything that ran",
  "Managed by us, or an install of your own",
];

/** The close: the mantra (00-META/mission.md § Mantra) and the paragraph under it. */
export const CLOSE: Part = {
  heading: "Decide once.",
  text: "Set the models, the tools and the rules for your workspace, and everything that connects is held to them. Sign in with the account you already have; the first sign-in creates your workspace.",
};

/** The sign-in card's lead on this page; its heading stays *Sign in*. */
export const CARD_LEAD = "The first sign-in creates a workspace for you.";

export const FOOTNOTE = "This page sets no cookies and loads nothing from elsewhere.";
